import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Loader2, RefreshCcw, Save, Plus, Trash2, Sparkles } from 'lucide-react';
import api from '@/lib/axios';

export default function MarketplaceCmsPanel() {
  const [content, setContent] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [novaFaq, setNovaFaq] = useState({ pergunta: '', resposta: '' });

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [c, f] = await Promise.all([
        api.get('/admin/marketplace/cms/content'), api.get('/admin/marketplace/cms/faqs'),
      ]);
      setContent(c.data.items || []); setFaqs(f.data.items || []);
    } catch (e) { setErr(e.response?.data?.detail || 'Falha a carregar.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seed = async () => { await api.post('/admin/marketplace/cms/seed'); load(); };
  const saveContent = async (c) => { await api.put(`/admin/marketplace/cms/content/${c.key}`, { valor: c.valor, grupo: c.grupo, label: c.label, publicado: c.publicado }); load(); };
  const addFaq = async () => {
    if (!novaFaq.pergunta.trim()) return;
    await api.post('/admin/marketplace/cms/faqs', { ...novaFaq, ordem: (faqs.length + 1) }); setNovaFaq({ pergunta: '', resposta: '' }); load();
  };
  const delFaq = async (id) => { await api.delete(`/admin/marketplace/cms/faqs/${id}`); load(); };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="cms-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
          <FileText className="h-5 w-5 text-emerald-600" /> Conteúdo público (CMS)
        </h2>
        <div className="flex gap-2">
          <button type="button" onClick={seed} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600" data-testid="cms-seed">
            <Sparkles className="h-3.5 w-3.5" /> Semear
          </button>
          <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600">
            <RefreshCcw className="h-3.5 w-3.5" /> Atualizar
          </button>
        </div>
      </div>
      {err && <p className="mt-2 text-xs font-semibold text-red-600">{err}</p>}
      {loading ? (
        <p className="mt-4 flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> A carregar...</p>
      ) : (
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          <div data-testid="cms-content-list">
            <p className="text-xs font-bold text-slate-700">Blocos de conteúdo & SEO</p>
            <ul className="mt-2 space-y-2">
              {content.length === 0 && <li className="text-xs text-slate-500">Sem conteúdo. Use "Semear".</li>}
              {content.map((c, i) => (
                <li key={c.key} className="rounded-lg border border-slate-200 p-2">
                  <p className="text-[10px] font-semibold text-slate-500">{c.label || c.key} <span className="text-slate-400">({c.grupo})</span></p>
                  <div className="mt-1 flex gap-1">
                    <textarea value={c.valor || ''} onChange={(e) => { const v = [...content]; v[i] = { ...c, valor: e.target.value }; setContent(v); }}
                      rows={2} className="w-full rounded border border-slate-300 px-2 py-1 text-[11px]" data-testid={`cms-content-${c.key}`} />
                    <button type="button" onClick={() => saveContent(content[i])} className="rounded bg-emerald-600 px-2 text-white" data-testid={`cms-save-${c.key}`}>
                      <Save className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div data-testid="cms-faqs-list">
            <p className="text-xs font-bold text-slate-700">FAQs</p>
            <div className="mt-2 space-y-1 rounded-lg border border-slate-200 p-2">
              <input value={novaFaq.pergunta} onChange={(e) => setNovaFaq({ ...novaFaq, pergunta: e.target.value })} placeholder="Pergunta"
                className="w-full rounded border border-slate-300 px-2 py-1 text-[11px]" data-testid="cms-faq-new-q" />
              <input value={novaFaq.resposta} onChange={(e) => setNovaFaq({ ...novaFaq, resposta: e.target.value })} placeholder="Resposta"
                className="w-full rounded border border-slate-300 px-2 py-1 text-[11px]" data-testid="cms-faq-new-a" />
              <button type="button" onClick={addFaq} className="inline-flex items-center gap-1 rounded bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white" data-testid="cms-faq-add">
                <Plus className="h-3 w-3" /> Adicionar FAQ
              </button>
            </div>
            <ul className="mt-2 space-y-1.5">
              {faqs.map((f) => (
                <li key={f.id} className="flex items-start justify-between gap-2 rounded-lg border border-slate-200 p-2 text-[11px]">
                  <div><p className="font-semibold text-slate-800">{f.pergunta}</p><p className="text-slate-500">{f.resposta}</p></div>
                  <button type="button" onClick={() => delFaq(f.id)} className="text-red-500" data-testid={`cms-faq-del-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}
